import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "config/axiosConfig";
import InputField from "ui/InputField";
import { Chip } from "ui/Chip";
import IssueSearch, { Suggestion } from "components/Issues/IssueSearch";
import RankedDragList from "ui/RankedDragList";
import { GripVertical, ListChecks, ListOrdered, ListTodo, X, ChartBar } from "lucide-react";
import type { PollDraft, PollOptionDraft, PollOptionResponse, PollRankSummaryRow, PollResponse } from "types/poll";
import EntityCompactCard from "components/Entities/EntityCompactCard";
import type { Issue } from "util/constants";
import { apiRoutes } from "util/apiRoutes";
import ContentAwardButton from "components/Common/ContentAwardButton";

type PollMode = "display" | "edit";

/**
 * Props for the PollCard component.
 */
interface PollCardProps {
  mode?: PollMode;
  pollData?: PollResponse;
  draft?: PollDraft;
  maxOptions?: number;
  onDraftChange?: (draft: PollDraft) => void;
  onRefresh?: () => Promise<void>;
  /** When set, shows an award control for this poll's parent content entity. */
  contentAwardEntityType?: string;
  contentAwardEntityId?: number;
  contentAwardAuthorUserId?: number | string | null;
  contentAwardTargetLabel?: string | null;
}

/**
 * Extract a readable label from entity attributes.
 * @param attributes - Entity attributes from API.
 * @returns {string} - Label for display.
 */
const getEntityLabel = (attributes: Record<string, unknown>): string => {
  const title = typeof attributes.title === "string" ? attributes.title : "";
  const name = typeof attributes.name === "string" ? attributes.name : "";
  const cname = typeof attributes.cname === "string" ? attributes.cname : "";
  if (title) return title;
  if (name) return name;
  if (cname) return cname.replace(/_/g, " ");
  return "Untitled entity";
};

/**
 * Build a normalized suggestion for IssueSearch.
 */
const buildIssueSuggestion = (option: PollOptionDraft): Suggestion | null => {
  if (!option.option_entity_id || !option.option_entity_name) return null;
  return {
    type: "issue",
    id: option.option_entity_id,
    name: option.option_entity_name,
    cname: option.option_entity_cname || option.option_entity_name.replace(/\s+/g, "_"),
    wikipedia_link: "",
    subject_properties: undefined
  };
};

/**
 * Render a poll card for creation or voting.
 */
const PollCard: React.FC<PollCardProps> = ({
  mode = "display",
  pollData,
  draft,
  maxOptions = 6,
  onDraftChange,
  onRefresh,
  contentAwardEntityType,
  contentAwardEntityId,
  contentAwardAuthorUserId,
  contentAwardTargetLabel
}) => {
  const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entityLabels, setEntityLabels] = useState<Record<string, string>>({});
  const [issueEntities, setIssueEntities] = useState<Record<number, Issue>>({});
  const [showResults, setShowResults] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [rankDraftOptionIds, setRankDraftOptionIds] = useState<number[]>([]);
  const [responseLockOverride, setResponseLockOverride] = useState<boolean | null>(null);

  const isEditable = mode === "edit";
  const pollType = isEditable ? draft?.poll_type : pollData?.poll.poll_type;

  const voteCounts = useMemo(() => {
    if (!pollData) return {};
    const map: Record<number, number> = {};
    pollData.summary.forEach((row) => {
      map[row.option_id] = row.vote_count;
    });
    return map;
  }, [pollData]);

  /**
   * Rank summary mapping by option and rank number.
   */
  const rankSummaryByOption = useMemo(() => {
    if (!pollData || !Array.isArray(pollData.rank_summary)) {
      return {};
    }
    const rows = pollData.rank_summary as PollRankSummaryRow[];
    const map: Record<number, Record<number, number>> = {};
    rows.forEach((row) => {
      if (!map[row.option_id]) {
        map[row.option_id] = {};
      }
      map[row.option_id][row.rank_order] = row.vote_count;
    });
    return map;
  }, [pollData]);

  const totalResponses = pollData?.total_respondents || 0;

  const getOptionLabel = useCallback(
    (option: PollOptionResponse): string => {
      if (option.option_text) {
        return option.option_text;
      }
      if (option.option_entity_type && option.option_entity_id) {
        const key = `${option.option_entity_type}-${option.option_entity_id}`;
        return entityLabels[key] || `Entity ${option.option_entity_id}`;
      }
      return "Untitled option";
    },
    [entityLabels]
  );

  useEffect(() => {
    if (!pollData) return;
    const loadEntityLabels = async () => {
      const grouped: Record<string, number[]> = {};
      pollData.options.forEach((option) => {
        if (option.option_entity_type && option.option_entity_id) {
          const existing = grouped[option.option_entity_type] || [];
          grouped[option.option_entity_type] = [...existing, option.option_entity_id];
        }
      });

      const labelMap: Record<string, string> = {};
      const requests = Object.entries(grouped).map(async ([entityType, ids]) => {
        try {
          const response = await api.get(apiRoutes.entities.root, {
            params: {
              entityType,
              entityIds: JSON.stringify(ids)
            }
          });
          const data = response.data;
          const results = Array.isArray(data) ? data : Array.isArray(data?.entities) ? data.entities : [];
          results.forEach((result: { id: number; entity_type: string; attributes: Record<string, unknown> }) => {
            const label = getEntityLabel(result.attributes || {});
            const key = `${result.entity_type}-${result.id}`;
            labelMap[key] = label;
          });
        } catch (err) {
          // Ignore label fetch errors; fallback labels will be used.
        }
      });

      await Promise.all(requests);
      setEntityLabels(labelMap);
    };

    loadEntityLabels();
  }, [pollData]);

  useEffect(() => {
    if (!pollData) return;
    const issueOptionIds = pollData.options
      .filter((option) => option.option_entity_type === "issue" && option.option_entity_id)
      .map((option) => option.option_entity_id as number);
    if (issueOptionIds.length === 0) {
      setIssueEntities({});
      return;
    }

    /**
     * Ensure issue entity attributes are valid before storing.
     */
    const isIssueAttributes = (value: unknown): value is Issue => {
      if (!value || typeof value !== "object") {
        return false;
      }
      const record = value as { id?: unknown };
      return typeof record.id === "number";
    };

    const loadIssueEntities = async () => {
      try {
        const response = await api.get(apiRoutes.entities.root, {
          params: {
            entityType: "issue",
            entityIds: JSON.stringify(issueOptionIds)
          }
        });
        const data = response.data;
        const results = Array.isArray(data) ? data : Array.isArray(data?.entities) ? data.entities : [];
        const map: Record<number, Issue> = {};
        results.forEach((result: { id: number; attributes: Record<string, unknown> }) => {
          if (isIssueAttributes(result.attributes)) {
            map[result.id] = result.attributes;
          }
        });
        setIssueEntities(map);
      } catch (err) {
        setIssueEntities({});
      }
    };

    loadIssueEntities();
  }, [pollData]);

  useEffect(() => {
    if (pollData?.user_has_responded) {
      setShowResults(true);
    }
  }, [pollData?.user_has_responded]);

  useEffect(() => {
    if (!pollData) return;
    setResponseLockOverride(null);
  }, [pollData]);

  useEffect(() => {
    if (!pollData || pollData.poll.poll_type !== "rank_order") return;
    const ordered = pollData.user_has_responded && pollData.user_rank_order
      ? [...pollData.options].sort((a, b) => {
          const rankA = pollData.user_rank_order?.[a.id] ?? Number.MAX_SAFE_INTEGER;
          const rankB = pollData.user_rank_order?.[b.id] ?? Number.MAX_SAFE_INTEGER;
          return rankA - rankB;
        })
      : pollData.options;
    setRankDraftOptionIds(ordered.map((option) => option.id));
  }, [pollData]);

  useEffect(() => {
    if (!pollData) return;
    if (pollData.user_option_ids && pollData.user_option_ids.length > 0) {
      setSelectedOptionIds(pollData.user_option_ids);
    }
  }, [pollData]);

  const updateDraft = (next: PollDraft) => {
    if (!onDraftChange) return;
    onDraftChange(next);
  };

  const updateDraftOption = (index: number, updater: (option: PollOptionDraft) => PollOptionDraft) => {
    if (!draft) return;
    const updated = draft.options.map((option, idx) => (idx === index ? updater(option) : option));
    updateDraft({ ...draft, options: updated });
  };

  const handleAddOption = () => {
    if (!draft) return;
    if (draft.options.length >= maxOptions) return;
    updateDraft({
      ...draft,
      options: [
        ...draft.options,
        { id: `poll-option-${Date.now()}`, kind: "text", option_text: "" }
      ]
    });
  };

  const handleRemoveOption = (index: number) => {
    if (!draft) return;
    if (draft.options.length <= 2) return;
    const filtered = draft.options.filter((_, idx) => idx !== index);
    updateDraft({ ...draft, options: filtered });
  };

  const submitResponses = async (responses: { option_id: number; rank_order?: number }[]) => {
    if (!pollData || !onRefresh) return;
    try {
      setIsSubmitting(true);
      await api.post(apiRoutes.polls.responses(pollData.poll.id), { responses });
      await onRefresh();
      setShowResults(true);
    } catch (err) {
      setError("Failed to submit vote.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Submit multi-choice or rank-order draft responses.
   */
  const handleSubmitPendingResponses = async () => {
    if (!pollData || !onRefresh) return;
    setError(null);
    if (pollData.poll.poll_type === "multi_choice") {
      if (selectedOptionIds.length === 0) {
        setError("Select at least one option before submitting.");
        return;
      }
      setResponseLockOverride(true);
      await submitResponses(selectedOptionIds.map((id) => ({ option_id: id })));
      return;
    }
    if (pollData.poll.poll_type === "rank_order") {
      if (rankDraftOptionIds.length === 0) {
        setError("Rank at least one option before submitting.");
        return;
      }
      setResponseLockOverride(true);
      const responses = rankDraftOptionIds.map((id, index) => ({
        option_id: id,
        rank_order: index + 1
      }));
      await submitResponses(responses);
    }
  };

  const handleRemoveResponse = async () => {
    if (!pollData || !onRefresh) return;
    try {
      setIsRemoving(true);
      await api.delete(apiRoutes.polls.responses(pollData.poll.id));
      await onRefresh();
      setSelectedOptionIds([]);
      setRankDraftOptionIds([]);
      setShowResults(false);
      setResponseLockOverride(false);
    } catch (err) {
      setError("Failed to remove response.");
    } finally {
      setIsRemoving(false);
    }
  };

  /**
   * Render the percentage bar for a given count.
   */
  const renderResultBar = (count: number) => {
    const percent = totalResponses > 0 ? Math.round((count / totalResponses) * 100) : 0;
    return (
      <div className="poll-result-bar flex items-center gap-2">
        <div className="poll-result-track h-2 w-full rounded-full bg-eoe-icon dark:bg-eoe-dark-icon">
          <div
            className="poll-result-fill h-2 rounded-full bg-eoe-text-heading dark:bg-eoe-dark-text-heading"
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className="poll-result-percent text-xs text-right text-eoe-text-meta dark:text-eoe-dark-text-meta min-w-8">
          {percent}%
        </span>
      </div>
    );
  };

  /**
   * Determine the most common rank for an option.
   */
  const getMostCommonRank = (optionId: number) => {
    const rankCounts = rankSummaryByOption[optionId] || {};
    let bestRank = 0;
    let bestCount = 0;
    Object.entries(rankCounts).forEach(([rank, count]) => {
      const numericRank = Number(rank);
      if (!Number.isInteger(numericRank)) {
        return;
      }
      if (count > bestCount || (count === bestCount && numericRank > bestRank)) {
        bestRank = numericRank;
        bestCount = count;
      }
    });
    return { rank: bestRank, count: bestCount };
  };

  const isMultiChoice = pollData?.poll.poll_type === "multi_choice";
  const isRankOrder = pollData?.poll.poll_type === "rank_order";
  const isResponseLocked = responseLockOverride ?? Boolean(pollData?.user_has_responded);

  const orderedOptions = useMemo(() => {
    if (!pollData) return [];
    if (pollData.poll.poll_type !== "rank_order" || !pollData.user_has_responded) {
      return pollData.options;
    }
    return [...pollData.options].sort((a, b) => {
      const rankA = pollData.user_rank_order?.[a.id] ?? Number.MAX_SAFE_INTEGER;
      const rankB = pollData.user_rank_order?.[b.id] ?? Number.MAX_SAFE_INTEGER;
      return rankA - rankB;
    });
  }, [pollData]);

  const rankDraftOptions = useMemo(() => {
    if (!pollData || pollData.poll.poll_type !== "rank_order") return [];
    return rankDraftOptionIds
      .map((id) => pollData.options.find((option) => option.id === id))
      .filter((option): option is PollOptionResponse => Boolean(option));
  }, [pollData, rankDraftOptionIds]);

  const excludedRankOptions = useMemo(() => {
    if (!pollData || pollData.poll.poll_type !== "rank_order") return [];
    const selectedIds = new Set(rankDraftOptionIds);
    return pollData.options.filter((option) => !selectedIds.has(option.id));
  }, [pollData, rankDraftOptionIds]);

  const rankResultOrder = useMemo(() => {
    if (!pollData || pollData.poll.poll_type !== "rank_order" || !showResults) {
      return orderedOptions;
    }
    if (totalResponses === 0) {
      return pollData.options;
    }
    const maxRank = pollData.options.length;
    const scores = pollData.options.map((option) => {
      const totalVotes = voteCounts[option.id] || 0;
      const missingCount = Math.max(0, totalResponses - totalVotes);
      const counts = rankSummaryByOption[option.id] || {};
      let weightedSum = 0;
      let weightedSqSum = 0;
      Object.entries(counts).forEach(([rank, count]) => {
        const numericRank = Number(rank);
        if (!Number.isInteger(numericRank)) return;
        weightedSum += numericRank * count;
        weightedSqSum += numericRank * numericRank * count;
      });
      if (missingCount > 0) {
        const missingRank = maxRank + 1;
        weightedSum += missingRank * missingCount;
        weightedSqSum += missingRank * missingCount * missingRank;
      }
      const mean = weightedSum / totalResponses;
      const variance = Math.max(0, weightedSqSum / totalResponses - mean * mean);
      const score = mean + Math.sqrt(variance);
      return { option, score, totalVotes };
    });
    scores.sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score;
      if (a.totalVotes !== b.totalVotes) return b.totalVotes - a.totalVotes;
      return a.option.id - b.option.id;
    });
    return scores.map((entry) => entry.option);
  }, [orderedOptions, pollData, rankSummaryByOption, showResults, totalResponses, voteCounts]);

  const rankDisplayMap = useMemo(() => {
    if (!pollData || pollData.poll.poll_type !== "rank_order" || !showResults) {
      return {};
    }
    const map: Record<number, number> = {};
    rankResultOrder.forEach((option, index) => {
      map[option.id] = index + 1;
    });
    return map;
  }, [pollData, rankResultOrder, showResults]);

  if (isEditable) {
    if (!draft) {
      return null;
    }
    return (
      <div className="poll-card-container poll-card-edit mt-1 rounded-2xl p-1">
        <InputField
          labelText="Poll statement"
          value={draft.statement}
          onChange={(event) => updateDraft({ ...draft, statement: event.target.value })}
          className="poll-statement-input"
        />

        <div className="poll-card-options mt-4 flex flex-col gap-4">
          {draft.options.map((option, index) => (
            <div key={option.id} className="poll-edit-option flex flex-col gap-3">
              <div className="poll-option-controls flex items-center justify-between">
                <div className="poll-option-type-chips flex gap-2">
                  <Chip
                    label="Text"
                    clickable
                    className="poll-option-chip-text"
                    variant={option.kind === "text" ? "primary" : "default"}
                    onClick={() =>
                      updateDraftOption(index, (current) => ({
                        ...current,
                        kind: "text",
                        option_entity_id: undefined,
                        option_entity_type: undefined,
                        option_entity_label: undefined,
                        option_entity_name: undefined,
                        option_entity_cname: undefined
                      }))
                    }
                  />
                  <Chip
                    label="Issue"
                    clickable
                    className="poll-option-chip-issue"
                    variant={option.kind === "entity" ? "primary" : "default"}
                    onClick={() =>
                      updateDraftOption(index, (current) => ({
                        ...current,
                        kind: "entity",
                        option_text: undefined
                      }))
                    }
                  />
                </div>
                {draft.options.length > 2 && (
                  <button
                    type="button"
                    className="poll-option-remove text-xs text-warning-red dark:text-dark-warning-red"
                    onClick={() => handleRemoveOption(index)}
                  >
                    Remove
                  </button>
                )}
              </div>

              {option.kind === "text" ? (
                <InputField
                  labelText="Option text"
                  value={option.option_text || ""}
                  onChange={(event) =>
                    updateDraftOption(index, (current) => ({
                      ...current,
                      option_text: event.target.value
                    }))
                  }
                  className="poll-option-text-input"
                />
              ) : (
                <IssueSearch
                  inputType="add"
                  placeholder="Search issue"
                  prepopulateData={buildIssueSuggestion(option)}
                  onSelect={(suggestion) => {
                    updateDraftOption(index, (current) => ({
                      ...current,
                      option_entity_type: "issue",
                      option_entity_id: suggestion.type === "issue" ? suggestion.id : undefined,
                      option_entity_label: suggestion.name,
                      option_entity_name: suggestion.name,
                      option_entity_cname: suggestion.cname
                    }));
                  }}
                  onEntityChipClose={() =>
                    updateDraftOption(index, (current) => ({
                      ...current,
                      option_entity_id: undefined,
                      option_entity_label: undefined,
                      option_entity_name: undefined,
                      option_entity_cname: undefined
                    }))
                  }
                />
              )}
            </div>
          ))}
        </div>

        <div className="poll-edit-actions mt-4 flex items-center justify-between">
          <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
            {draft.options.length}/{maxOptions} options
          </span>
          <button
            type="button"
            className="poll-option-add btn-secondary px-3 py-1 text-xs"
            onClick={handleAddOption}
            disabled={draft.options.length >= maxOptions}
          >
            Add option
          </button>
        </div>

        {pollType === "rank_order" && (
          <div className="poll-rank-edit mt-4">
            <div className="text-xs font-semibold text-eoe-text-meta dark:text-eoe-dark-text-meta mb-2">
              Drag to set rank order
            </div>
            <RankedDragList
              items={draft.options}
              getId={(item) => item.id}
              onReorder={(items) => updateDraft({ ...draft, options: items })}
              renderItem={(item, handleProps) => (
                <div className="poll-rank-item flex items-center gap-2 rounded-xl border border-eoe-border-main dark:border-eoe-dark-border-main p-2">
                  <span {...handleProps} className="poll-rank-handle cursor-grab">
                    <GripVertical className="size-5" />
                  </span>
                  <span className="text-sm text-eoe-text dark:text-eoe-dark-text">
                    {item.kind === "text" ? item.option_text || "Untitled option" : item.option_entity_label || "Issue option"}
                  </span>
                </div>
              )}
            />
          </div>
        )}
      </div>
    );
  }

  if (!pollData || !onRefresh) {
    return null;
  }

  return (
    <div className="poll-card-container mt-1 rounded-2xl p-1">
      <div className="poll-card-header flex flex-wrap items-center gap-2 text-sm font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
        <ChartBar className="size-4 flex-shrink-0" />
        <span className="min-w-0 flex-1">{pollData.poll.statement}</span>
        {contentAwardEntityType && contentAwardEntityId != null ? (
          <ContentAwardButton
            entityType={contentAwardEntityType}
            entityId={contentAwardEntityId}
            authorUserId={contentAwardAuthorUserId}
            targetLabel={contentAwardTargetLabel || pollData.poll.statement}
            compact
          />
        ) : null}
      </div>
      <div className="poll-card-type flex items-center gap-2 text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
        {pollData.poll.poll_type === "single_choice" && <><ListTodo className="size-4 flex-shrink-0" /> Single choice</>}
        {pollData.poll.poll_type === "multi_choice" && <><ListChecks className="size-4 flex-shrink-0" /> Multiple choice</>}
        {pollData.poll.poll_type === "rank_order" && <><ListOrdered className="size-4 flex-shrink-0" /> Rank order</>}
      </div>
      <div className="poll-card-options mt-3 flex flex-col gap-3">
        {isRankOrder ? (
          <div className="poll-rank-order-wrapper flex flex-col gap-2">
            <RankedDragList
              items={showResults ? rankResultOrder : rankDraftOptions}
              getId={(item) => item.id.toString()}
              onReorder={(items) => {
                if (isSubmitting || isResponseLocked || showResults) return;
                setRankDraftOptionIds(items.map((option) => option.id));
              }}
              renderItem={(option, handleProps) => {
                const label = getOptionLabel(option);
                const { count: rankCount } = getMostCommonRank(option.id);
                const displayCount = showResults ? rankCount : voteCounts[option.id] || 0;
                const issueEntity = option.option_entity_type === "issue" && option.option_entity_id
                  ? issueEntities[option.option_entity_id]
                  : null;
                const canRemoveRank = !isResponseLocked && !showResults && rankDraftOptions.length > 1;
                return (
                  <div className="poll-option-rank flex flex-col gap-1 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 select-none">
                        {!isResponseLocked && !showResults && (
                          <span {...handleProps} className="poll-option-handle cursor-grab text-eoe-text-meta dark:text-eoe-dark-text-meta">
                            <GripVertical className="size-5" />
                          </span>
                        )}
                        {showResults && rankDisplayMap[option.id] && (
                          <div className="flex flex-col items-center gap-2">
                            <span className="poll-option-rank-number text-nowrap text-sm font-semibold rounded">
                              #{rankDisplayMap[option.id]}
                            </span>
                            {pollData.user_has_responded && pollData.user_rank_order?.[option.id] ? (
                          <span className="poll-option-user-rank-value px-1.5 py-0.5 text-xxs text-eoe-text-meta dark:text-eoe-dark-text-meta rounded-sm outline outline-1">
                            {pollData.user_rank_order[option.id]}
                          </span>
                        ) : (
                          <X className="size-4 text-eoe-text-meta dark:text-eoe-dark-text-meta" />
                        )}
                          </div>
                        )}
                        {issueEntity ? (
                          <div className="poll-option-issue-card w-full">
                            <EntityCompactCard
                              entity={issueEntity}
                              displayEntity={issueEntity}
                              entityId={issueEntity.id}
                              entityType="issue"
                              showActions={false}
                              showMeta={false}
                            />
                          </div>
                        ) : (
                          <span>{label}</span>
                        )}
                      </div>
                    {canRemoveRank && (
                      <button
                        type="button"
                        className="poll-option-rank-remove text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-warning-red dark:hover:text-warning-red-dark"
                        onClick={() => setRankDraftOptionIds((prev) => prev.filter((id) => id !== option.id))}
                        aria-label="Remove ranked option"
                      >
                        <X className="size-4" />
                      </button>
                    )}
                    </div>
                    {showResults && renderResultBar(displayCount)}
                  </div>
                );
              }}
            />
            {!isResponseLocked && !showResults && excludedRankOptions.length > 0 && (
              <div className="poll-rank-excluded-options flex flex-wrap gap-2">
                {excludedRankOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className="poll-rank-excluded-button text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta border border-eoe-border-main dark:border-eoe-dark-border-main rounded-full px-3 py-1 hover:text-eoe-text dark:hover:text-eoe-dark-text"
                    onClick={() => setRankDraftOptionIds((prev) => [...prev, option.id])}
                  >
                    Add {getOptionLabel(option)}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          orderedOptions.map((option) => {
            const optionLabel = getOptionLabel(option);
            const count = voteCounts[option.id] || 0;
            const isSelected = selectedOptionIds.includes(option.id);
            const issueEntity = option.option_entity_type === "issue" && option.option_entity_id
              ? issueEntities[option.option_entity_id]
              : null;
            const handleOptionClick = async () => {
              if (isSubmitting) return;
              if (isResponseLocked) return;
              setError(null);
              if (pollData.poll.poll_type === "single_choice") {
                setSelectedOptionIds([option.id]);
                await submitResponses([{ option_id: option.id }]);
                return;
              }
              const nextSelection = isSelected
                ? selectedOptionIds.filter((id) => id !== option.id)
                : [...selectedOptionIds, option.id];
              setSelectedOptionIds(nextSelection);
            };

            return (
              <div key={option.id} className="poll-option-item flex flex-col gap-1 text-sm">
                <div className="poll-option-input-wrapper relative">
                  {issueEntity ? (
                    <div
                      className={`poll-option-issue-card ${isSelected ? "ring-2 ring-eoe-text-heading dark:ring-eoe-dark-text-heading rounded-lg" : "rounded-lg"}`}
                    >
                      <EntityCompactCard
                        entity={issueEntity}
                        displayEntity={issueEntity}
                        entityId={issueEntity.id}
                        entityType="issue"
                        showActions={false}
                        showMeta={true}
                      />
                    </div>
                  ) : (
                    <InputField
                      value={optionLabel}
                      disabled={true}
                      variant="poll"
                      clickable={true}
                      className="poll-option-input-field"
                    />
                  )}
                  <button
                    type="button"
                    className={`poll-option-click-overlay absolute inset-0 ${isSelected ? "ring-2 ring-eoe-text-heading dark:ring-eoe-dark-text-heading" : ""} ${issueEntity ? "rounded-lg" : "rounded-lg"}`}
                    onClick={handleOptionClick}
                  />
                </div>
                {showResults && renderResultBar(count)}
              </div>
            );
          })
        )}
      </div>

      {error && (
        <div className="poll-card-error mt-3 text-sm text-warning-red dark:text-dark-warning-red">
          {error}
        </div>
      )}

      <div className="poll-card-actions mt-2 flex flex-col gap-3">
        <div className="poll-card-results-toggle flex items-center justify-between">
          <button
            type="button"
            className={`poll-view-results text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text ${showResults ? 'hidden' : ''}`}
            onClick={() => setShowResults((prev) => !prev)}
          >
            {showResults ? "" : "View results"}
          </button>
          {showResults && (
            <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
              {totalResponses} response{totalResponses === 1 ? "" : "s"}
            </span>
          )}
          <div className="poll-card-action-buttons flex flex-col gap-2">
            {isMultiChoice && !pollData.user_has_responded && (
              <button
                type="button"
                className="poll-submit-response text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                onClick={handleSubmitPendingResponses}
                disabled={isSubmitting || selectedOptionIds.length === 0}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            )}
            {isRankOrder && !pollData.user_has_responded && (
              <button
                type="button"
                className="poll-submit-response text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                onClick={handleSubmitPendingResponses}
                disabled={isSubmitting || rankDraftOptionIds.length === 0}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            )}
            {pollData.user_has_responded && (
              <button
                type="button"
                className="poll-remove-response text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                onClick={handleRemoveResponse}
                disabled={isRemoving}
              >
                {isRemoving ? "Removing..." : "Remove response"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PollCard;
